/**
 * Offboarding request detail — `GET offboarding/requests/{id}/`.
 *
 * The response is rich: nested `inspection_items[]`, `settlement` (nullable —
 * null until a `DepositSettlement` row exists), `lease_details`, and
 * `initiated_by_details` (a full nested `User`). All of it is rendered here,
 * not just the top-level scalar fields.
 *
 * `calculate_reconciliation`/`complete` are manager-only `detail=True`
 * actions, distinct from a generic PATCH — see the backend route file's top
 * comment. Adding an inspection item (on its own page) re-runs reconciliation
 * automatically server-side, but EDITING an existing item does not — that gap
 * is ported as-is, not "fixed" here.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  calculateReconciliation,
  completeOffboarding,
  deleteOffboardingRequest,
  getOffboardingRequest,
} from '../../api/endpoints/offboarding';

/**
 * Mirrors `REFUND_REMINDER_DAYS` in
 * refactor/backend/src/jobs/checkDepositRefundOverdue.ts — an operational
 * reminder default (Kenya sets no statutory deposit-refund deadline), not a
 * value read from the API. Kept in sync by hand; if the backend constant
 * changes, update this one too.
 */
const REFUND_REMINDER_DAYS = 30;

/** Purely a client-side computation from data the detail response already carries — no new API field. */
function refundDueInfo(
  completedAt: string | null,
  settlement: { is_paid: boolean } | null,
): { dueDate: string; isOverdue: boolean } | null {
  if (completedAt === null || settlement === null || settlement.is_paid) return null;
  const due = new Date(completedAt);
  due.setDate(due.getDate() + REFUND_REMINDER_DAYS);
  return { dueDate: due.toLocaleDateString(), isOverdue: due.getTime() <= Date.now() };
}

export function OffboardingDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const canManage = user?.role === 'LANDLORD' || user?.role === 'AGENT' || user?.role === 'SUPERAGENT';

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);

  const query = useQuery({
    queryKey: ['offboarding-request', requestId],
    queryFn: () => getOffboardingRequest(requestId),
    enabled: Number.isFinite(requestId),
  });

  const remove = useMutation({
    mutationFn: () => deleteOffboardingRequest(requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['offboarding-requests'] });
      navigate('..');
    },
  });

  const recalculate = useMutation({
    mutationFn: () => calculateReconciliation(requestId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['offboarding-request', requestId] }),
  });

  const complete = useMutation({
    mutationFn: () => completeOffboarding(requestId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['offboarding-request', requestId] }),
  });

  if (!Number.isFinite(requestId)) {
    return <p>Invalid offboarding request id.</p>;
  }

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const request = query.data;
  const settlement = request.settlement;
  const refundDue = refundDueInfo(request.completed_at, settlement);

  return (
    <section>
      <h2>
        Offboarding — {request.lease_details.tenant_name} ({request.lease_details.unit_number})
      </h2>

      {canManage && (
        <p>
          <button type="button" className="button--danger" onClick={() => setConfirmDeleteOpen(true)}>
            Delete
          </button>
        </p>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete offboarding request"
        message="Delete this offboarding request? This cannot be undone."
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          setConfirmDeleteOpen(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {remove.isError && (
        <p role="alert" className="form__error">
          {parseApiError(remove.error).message}
        </p>
      )}

      <dl>
        <dt>Status</dt>
        <dd>{request.status}</dd>
        <dt>Property</dt>
        <dd>{request.lease_details.property_name}</dd>
        <dt>Unit</dt>
        <dd>{request.lease_details.unit_number}</dd>
        <dt>Tenant</dt>
        <dd>{request.lease_details.tenant_name}</dd>
        <dt>Lease ends</dt>
        <dd>{request.lease_details.end_date ?? '—'}</dd>
        <dt>Initiated</dt>
        <dd>{request.initiated_at}</dd>
        <dt>Completed</dt>
        <dd>{request.completed_at ?? '—'}</dd>
        <dt>Notes</dt>
        <dd>{request.notes === '' ? '—' : request.notes}</dd>
      </dl>

      <h3>Initiated by</h3>
      {request.initiated_by_details === null ? (
        <p>—</p>
      ) : (
        <dl>
          <dt>Name</dt>
          <dd>
            {request.initiated_by_details.first_name} {request.initiated_by_details.last_name}
          </dd>
          <dt>Email</dt>
          <dd>{request.initiated_by_details.email}</dd>
          <dt>Role</dt>
          <dd>{request.initiated_by_details.role}</dd>
          <dt>Current balance</dt>
          <dd>{request.initiated_by_details.current_balance}</dd>
        </dl>
      )}

      <h3>Inspection items ({request.inspection_items.length})</h3>
      {canManage && (
        <p>
          <Link to="inspections/new">Add inspection item</Link>
        </p>
      )}
      {request.inspection_items.length === 0 && <p>No inspection items recorded.</p>}
      {request.inspection_items.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Condition</th>
              <th>Damaged</th>
              <th>Repair cost</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {request.inspection_items.map((item) => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{item.condition === '' ? '—' : item.condition}</td>
                <td>{item.is_damaged ? 'Yes' : 'No'}</td>
                <td>{item.repair_cost}</td>
                <td>{item.notes === '' ? '—' : item.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Deposit settlement</h3>
      {settlement === null ? (
        <p>Not yet settled. Recalculate reconciliation to generate one.</p>
      ) : (
        <dl>
          <dt>Total outstanding fees</dt>
          <dd>{settlement.total_outstanding_fees}</dd>
          <dt>Repair charges</dt>
          <dd>{settlement.repair_charges}</dd>
          <dt>Cleaning fees</dt>
          <dd>{settlement.cleaning_fees}</dd>
          <dt>Other deductions</dt>
          <dd>{settlement.other_deductions}</dd>
          <dt>Deduction notes</dt>
          <dd>{settlement.deduction_notes === '' ? '—' : settlement.deduction_notes}</dd>
          <dt>Refund amount</dt>
          <dd>{settlement.refund_amount}</dd>
          <dt>Additional amount due</dt>
          <dd>{settlement.additional_amount_due}</dd>
          <dt>Paid</dt>
          <dd>{settlement.is_paid ? 'Yes' : 'No'}</dd>
          <dt>Settled at</dt>
          <dd>{settlement.settled_at ?? '—'}</dd>
          {refundDue !== null && (
            <>
              <dt>Refund due by</dt>
              <dd style={{ color: refundDue.isOverdue ? '#dc2626' : undefined }}>
                {refundDue.dueDate}
                {refundDue.isOverdue ? ' (overdue)' : ''}
              </dd>
            </>
          )}
        </dl>
      )}

      {canManage && (
        <>
          <h3>Actions</h3>
          {recalculate.isError && (
            <p role="alert" className="form__error">
              {parseApiError(recalculate.error).message}
            </p>
          )}
          {complete.isError && (
            <p role="alert" className="form__error">
              {parseApiError(complete.error).message}
            </p>
          )}
          <p>
            <button type="button" disabled={recalculate.isPending} onClick={() => recalculate.mutate()}>
              {recalculate.isPending ? 'Recalculating…' : 'Recalculate reconciliation'}
            </button>{' '}
            <button type="button" disabled={complete.isPending} onClick={() => setConfirmCompleteOpen(true)}>
              {complete.isPending ? 'Completing…' : 'Complete offboarding'}
            </button>
          </p>
        </>
      )}

      <ConfirmDialog
        open={confirmCompleteOpen}
        title="Complete offboarding"
        message="Complete this offboarding? This terminates the lease."
        confirmLabel="Complete"
        onConfirm={() => {
          setConfirmCompleteOpen(false);
          complete.mutate();
        }}
        onCancel={() => setConfirmCompleteOpen(false)}
      />
    </section>
  );
}

export default OffboardingDetailPage;
